import React from 'react';
import { Tag, Tooltip } from 'antd';

const SITUATION_LABEL: { [key: string]: string } = {
    PENDING: 'Pendente',
    NAO_INTEGRADO: 'Não integrado',
};

const CATEGORY_LABEL: { [key: string]: string } = {
    ERRO: 'Erro',
    NAO_IMPLEMENTADO: 'Falta implementação',
};

/** Valor já em reais (o contrato do diagnóstico não usa centavos). */
export const formatToBRL = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined || isNaN(Number(amount))) return '-';
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(Number(amount));
};

const FIELD_LABEL: { [path: string]: string } = {
    type: 'Tipo de pessoa',
    firstName: 'Nome',
    lastName: 'Sobrenome',
    corporateName: 'Razão social',
    doingBusiness: 'Nome fantasia',
    email: 'E-mail',
    phone: 'Telefone',
    originCountry: 'País de origem',
    'document.number': 'Número do documento',
    'document.type': 'Tipo do documento',
    'address.typeAddress': 'Tipo de logradouro',
    'address.street': 'Rua',
    'address.number': 'Número',
    'address.complement': 'Complemento',
    'address.district': 'Bairro',
    'address.city': 'Cidade',
    'address.state': 'UF',
    'address.country': 'País',
    'address.postalCode': 'CEP',
};

/** Códigos de erro do Salesforce ("first error: CODE") com tradução curta. */
const SF_ERROR_LABEL: { [code: string]: string } = {
    CANNOT_INSERT_UPDATE_ACTIVATE_ENTITY: 'Automação (trigger) do Salesforce falhou ao processar o pedido',
    FIELD_CUSTOM_VALIDATION_EXCEPTION: 'Regra de validação do Salesforce rejeitou o pedido',
    REQUIRED_FIELD_MISSING: 'Campo obrigatório ausente no Salesforce',
    DUPLICATE_VALUE: 'Registro duplicado no Salesforce',
    UNABLE_TO_LOCK_ROW: 'Registro bloqueado no Salesforce — tente reenviar mais tarde',
    INVALID_CROSS_REFERENCE_KEY: 'Referência inválida entre registros no Salesforce',
    STRING_TOO_LONG: 'Valor maior que o limite de um campo no Salesforce',
    MALFORMED_ID: 'ID malformado enviado ao Salesforce',
    INVALID_FIELD: 'Campo inexistente ou inválido no Salesforce',
};

const MAX_SUMMARY = 140;

export interface IReasonView {
    /** Frase curta em pt-BR para exibição. */
    summary: string,
    /** Texto original completo, presente quando difere do resumo. */
    details?: string,
}

/** `customer.document.number is required` → campo pt-BR; null quando o trecho não é erro Joi. */
const parseJoiSegment = (segment: string): { field: string, required: boolean } | null => {
    const match = segment.match(/^"?customer\.([\w.]+)"? (.+)$/);
    if (!match) return null;
    const predicate = match[2].toLowerCase();
    return {
        field: FIELD_LABEL[match[1]] || match[1],
        required: predicate.indexOf('is required') !== -1 || predicate.indexOf('not allowed to be empty') !== -1,
    };
};

/**
 * Converte o `reason` cru do backend (erros Joi concatenados por ' | ', com
 * repetições, ou exceções do Salesforce) em um resumo legível, preservando o
 * texto original em `details`.
 */
export const parseReason = (reason: string | null | undefined): IReasonView => {
    const raw = (reason || '').trim();
    if (!raw) return { summary: '-' };

    const segments = raw.split('|').map(s => s.trim()).filter(Boolean)
        .filter((s, i, all) => all.indexOf(s) === i);
    const joi = segments.map(parseJoiSegment);

    if (segments.length > 0 && joi.every(j => j !== null)) {
        const unique = (fields: string[]) => fields.filter((f, i) => fields.indexOf(f) === i).join(', ');
        const required = unique(joi.filter(j => j && j.required).map(j => j!.field));
        const invalid = unique(joi.filter(j => j && !j.required).map(j => j!.field));
        const parts: string[] = [];
        if (required) parts.push(`Campos obrigatórios não preenchidos: ${required}`);
        if (invalid) parts.push(`Campos inválidos: ${invalid}`);
        return { summary: parts.join(' · '), details: raw };
    }

    const sf = raw.match(/first error:\s*([A-Z_0-9]+)/);
    if (sf) {
        const label = SF_ERROR_LABEL[sf[1]] || 'Erro retornado pelo Salesforce';
        return { summary: `${label} (${sf[1]})`, details: raw };
    }

    if (raw.length > MAX_SUMMARY) {
        return { summary: `${raw.slice(0, MAX_SUMMARY)}…`, details: raw };
    }

    return { summary: raw };
};

/** Resumo com o texto original em tooltip — para a coluna Motivo da lista. */
export const ReasonCell = ({ reason }: { reason: string | null | undefined }) => {
    const { summary, details } = parseReason(reason);
    if (!details) return <>{summary}</>;
    return (
        <Tooltip title={details} placement="topLeft" overlayClassName="diagnostic-reason-tooltip">
            <span className="diagnostic-reason-summary">{summary}</span>
        </Tooltip>
    );
};

/** Resumo com o texto original abaixo — para o Motivo dentro do modal. */
export const ReasonDetail = ({ reason }: { reason: string | null | undefined }) => {
    const { summary, details } = parseReason(reason);
    return (
        <>
            {summary}
            {details && details !== summary && <div className="diagnostic-reason-raw">{details}</div>}
        </>
    );
};

export const SituationTag = ({ situation }: { situation: string }) => (
    <Tag className={`tags-status ${situation === 'PENDING' ? 'diagnostic-pending' : 'diagnostic-nao-integrado'}`}>
        {SITUATION_LABEL[situation] || situation}
    </Tag>
);

export const CategoryTag = ({ category }: { category: string }) => (
    <Tag className={`tags-status ${category === 'ERRO' ? 'diagnostic-erro' : 'diagnostic-nao-implementado'}`}>
        {CATEGORY_LABEL[category] || category}
    </Tag>
);
